import { GuardedDeletionService } from 'projects/orchestrator/src/lib/services/guarded-deletion.service';
import { OnInit, Component, DestroyRef, inject } from '@angular/core';
import { OfficialPlannedPlantationHeaderService } from '../proxy/operational/controllers';
import { ListService } from '@abp/ng.core';
import {
  ConfirmationService,
} from '@abp/ng.theme.shared';
import {
  OfficialPlannedPlantationHeaderCreateDto,
  OfficialPlannedPlantationHeaderUpdateDto,
} from '../proxy/operational/dtos/official-planned-plantation-header';
import { ACTION_IDS, OFFICIALPLANNEDPLANTATIONHEADER_ACTIONS, OFFICIALPLANNEDPLANTATIONHEADER_CONFIG, OFFICIALPLANNEDPLANTATIONHEADER_FIELDS } from './official-planned-plantation-header.config';
import { BaseHierarchicalCrudComponent, createImportExcelModalConfig, ExcelImportErrorsExportLike, ExcelImportResultLike, GenericForm, IActionsSchema, IMPORT_AGAIN_LABEL, IMPORT_MAX_SIZE_MB, IMPORT_MODAL_TITLE, ModalBuilder, resolveActions, ZnExcelImportErrorsModal, ZnFileUploader, ZnHierarchicalViewComponent } from '@zendra/shared';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { OfficialPlannedPlantationHeaderPlanningOrchestratorService, OfficialPlannedPlantationHeaderProjectionPlanningOrchestratorService } from 'orchestrator';
import {
  GetOfficialPlannedPlantationHeaderProjectionListInputDto,
  OfficialPlannedPlantationHeaderProjectionDto,
  OfficialPlannedPlantationHeaderProjectionExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/official-planned-plantation-headers/models';

const HEADER_IMPORT_TEMPLATE_FILE_NAME = 'OfficialPlannedPlantationHeaderImportTemplate.xlsx';
const STAND_IMPORT_TEMPLATE_FILE_NAME = 'OfficialPlannedPlantationStandImportTemplate.xlsx';

type OfficialPlannedPlantationHeaderImportResult = ExcelImportResultLike<unknown> & {
  fileName?: string;
};

@Component({
  selector: 'app-official-planned-plantation-header',
  imports: [CommonModule, GenericForm, ZnHierarchicalViewComponent],
  providers: [ListService, OfficialPlannedPlantationHeaderService, ConfirmationService],
  templateUrl: './official-planned-plantation-header.html',
  styleUrl: './official-planned-plantation-header.scss',
})
export class OfficialPlannedPlantationHeader extends BaseHierarchicalCrudComponent<
  OfficialPlannedPlantationHeaderProjectionDto,
  OfficialPlannedPlantationHeaderCreateDto,
  OfficialPlannedPlantationHeaderUpdateDto,
  OfficialPlannedPlantationHeaderProjectionExcelDownloadDto
> implements OnInit {
  private readonly guardedDeletion = inject(GuardedDeletionService);
  private readonly operationalService = inject(OfficialPlannedPlantationHeaderService);

  private readonly orchestratorService = inject(OfficialPlannedPlantationHeaderPlanningOrchestratorService);
  private readonly projectionOrchestratorService = inject(OfficialPlannedPlantationHeaderProjectionPlanningOrchestratorService);
  private readonly modalService = inject(ModalBuilder);
  private readonly destroyRef = inject(DestroyRef);

  config = OFFICIALPLANNEDPLANTATIONHEADER_CONFIG;
  fields = OFFICIALPLANNEDPLANTATIONHEADER_FIELDS;
  actions: IActionsSchema | null = null;

  service = {
    create: (input: OfficialPlannedPlantationHeaderCreateDto) => this.operationalService.create(input),
    update: (id: number, input: OfficialPlannedPlantationHeaderUpdateDto) => this.operationalService.update(id, input),
    deleteByIds: ids => this.guardedDeletion.deleteByIds('official-planned-plantation-headers', ids),
    getList: (input: GetOfficialPlannedPlantationHeaderProjectionListInputDto) =>
      this.projectionOrchestratorService.getList(input),
    getDownloadToken: () => this.projectionOrchestratorService.getDownloadToken(),
    getListAsExcelFile: (input: OfficialPlannedPlantationHeaderProjectionExcelDownloadDto) =>
      this.projectionOrchestratorService.getListAsExcelFile(input),
    projectionConsistency: this.createProjectionConsistency(
      this.projectionOrchestratorService,
      'officialPlannedPlantationHeaderId',
    ),
  };

  private readonly triggerMap: Record<number, () => void> = {
    [ACTION_IDS.importExcelModal]: () => this.openHeaderImportModal(),
    [ACTION_IDS.importStandsExcelModal]: () => this.openStandImportModal(),
    [ACTION_IDS.downloadExcelTemplateTrigger]: () =>
      this.downloadExcelTemplates(),
  };

  ngOnInit(): void {
    this.actions = resolveActions(
      OFFICIALPLANNEDPLANTATIONHEADER_ACTIONS,
      this.triggerMap,
    );
  }

  protected prepareUpdateDto(data: any, selected: OfficialPlannedPlantationHeaderProjectionDto): OfficialPlannedPlantationHeaderUpdateDto {
    return {
      periodId: data.periodId,
      vendorId: data.vendorId,
      propertyLocationId: data.propertyLocationId,
      signatureReasonId: data.signatureReasonId,
      dinamaReasonId: data.dinamaReasonId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'OfficialPlannedPlantationHeader';
  }

  private openHeaderImportModal(): void {
    const modalRef = this.modalService.openModal(
      ZnFileUploader,
      createImportExcelModalConfig({
        component: ZnFileUploader,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Operational::Entity.OfficialPlannedPlantationHeader',
        inputs: {
          maxSizeMb: IMPORT_MAX_SIZE_MB,
          importFile: (formData: FormData) =>
            this.orchestratorService.importOfficialPlannedPlantationHeader(formData),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: OfficialPlannedPlantationHeaderImportResult) => {
        if (result?.hasErrors) {
          this.openHeaderImportErrorsModal(result);
          return;
        }

        if (result) {
          this.refreshGrid();
        }
      });
  }

  private openHeaderImportErrorsModal(
    result: OfficialPlannedPlantationHeaderImportResult,
  ): void {
    const modalRef = this.modalService.openModal(
      ZnExcelImportErrorsModal,
      createImportExcelModalConfig({
        component: ZnExcelImportErrorsModal,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Operational::Entity.OfficialPlannedPlantationHeader',
        confirmLabel: IMPORT_AGAIN_LABEL,
        dismissLabel: 'LanguageService::Close',
        size: 'lg',
        inputs: {
          fileName: result.fileName ?? '',
          errors: result.errors,
          title: IMPORT_MODAL_TITLE,
          subTitle: 'Operational::Entity.OfficialPlannedPlantationHeader',
          exportErrors: (
            input: ExcelImportErrorsExportLike,
          ) =>
            this.orchestratorService.exportOfficialPlannedPlantationHeaderImportErrors(
              input,
            ),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((modalResult?: { retry?: boolean }) => {
        if (modalResult?.retry) {
          this.openHeaderImportModal();
        }
      });
  }


  private openStandImportModal(): void {
    const modalRef = this.modalService.openModal(
      ZnFileUploader,
      createImportExcelModalConfig({
        component: ZnFileUploader,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Operational::Entity.OfficialPlannedPlantation',
        inputs: {
          maxSizeMb: IMPORT_MAX_SIZE_MB,
          importFile: (formData: FormData) =>
            this.orchestratorService.importOfficialPlannedPlantationStand(formData),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: OfficialPlannedPlantationHeaderImportResult) => {
        if (result?.hasErrors) {
          this.openStandImportErrorsModal(result);
          return;
        }

        if (result) {
          this.refreshGrid();
        }
      });
  }

  private openStandImportErrorsModal(
    result: OfficialPlannedPlantationHeaderImportResult,
  ): void {
    const modalRef = this.modalService.openModal(
      ZnExcelImportErrorsModal,
      createImportExcelModalConfig({
        component: ZnExcelImportErrorsModal,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Operational::Entity.OfficialPlannedPlantation',
        confirmLabel: IMPORT_AGAIN_LABEL,
        dismissLabel: 'LanguageService::Close',
        size: 'lg',
        inputs: {
          fileName: result.fileName ?? '',
          errors: result.errors,
          title: IMPORT_MODAL_TITLE,
          subTitle: 'Operational::Entity.OfficialPlannedPlantation',
          exportErrors: (
            input: ExcelImportErrorsExportLike,
          ) =>
            this.orchestratorService.exportOfficialPlannedPlantationStandImportErrors(
              input,
            ),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((modalResult?: { retry?: boolean }) => {
        if (modalResult?.retry) {
          this.openStandImportModal();
        }
      });
  }

  private downloadExcelTemplates(): void {
    forkJoin({
      headerTemplate: this.orchestratorService.getOfficialPlannedPlantationHeaderImportTemplate(),
      standTemplate: this.orchestratorService.getOfficialPlannedPlantationStandImportTemplate(),
    }).subscribe({
      next: ({ headerTemplate, standTemplate }) => {
        this.toaster.success(
          'LanguageService::Actions:DownloadTemplatesMessage',
          '',
          { life: 5000 },
        );
        this.downloadFile(
          headerTemplate,
          HEADER_IMPORT_TEMPLATE_FILE_NAME,
        );
        this.downloadFile(
          standTemplate,
          STAND_IMPORT_TEMPLATE_FILE_NAME,
        );
      },
      error: error => {
        this.toaster.error(error, '', {
          life: 5000,
        });
      },
    });
  }
}
